/**
 * ecosystemDisplay: shared presentation helpers for the platform apex
 * ecosystem surfaces (the hosted-ecosystems directory and the
 * ecosystem-of-ecosystems graph).
 *
 * These operate on the directory projection returned by `GET /api/ecosystems`
 * ({ slug, name, shortName, description, branding, memberCount }).
 */

// Max characters for a tagline derived from a description before truncation.
const TAGLINE_MAX_LENGTH = 110;

/**
 * Derive a short tagline for an ecosystem card.
 *
 * Prefers the ecosystem's `shortName` when it differs meaningfully from the
 * full `name` (an acronym like "COSTEM" reads well as a tagline). Otherwise
 * falls back to a truncated slice of the `description`. Returns an empty
 * string if neither is available.
 *
 * @param {object} ecosystem - A directory-projection ecosystem record.
 * @returns {string} A tagline suitable for a card subtitle.
 */
export function deriveTagline(ecosystem) {
    if (!ecosystem) return '';

    const description = (ecosystem.description || '').trim();
    if (description) {
        if (description.length <= TAGLINE_MAX_LENGTH) {
            return description;
        }
        // Truncate on a word boundary where possible, then add an ellipsis.
        const slice = description.slice(0, TAGLINE_MAX_LENGTH);
        const lastSpace = slice.lastIndexOf(' ');
        const trimmed = lastSpace > 0 ? slice.slice(0, lastSpace) : slice;
        return `${trimmed}…`;
    }

    const shortName = (ecosystem.shortName || '').trim();
    if (shortName && shortName !== (ecosystem.name || '').trim()) {
        return shortName;
    }

    return '';
}

/**
 * Resolve the node color for an ecosystem in the ecosystem-of-ecosystems
 * graph. Uses the ecosystem's branding primary color when set, else a
 * neutral default so the canvas renderer always has a valid color string.
 *
 * @param {object} ecosystem - A directory-projection ecosystem record.
 * @returns {string} A CSS color string.
 */
export function ecosystemNodeColor(ecosystem) {
    const primary = ecosystem?.branding?.primaryColor;
    if (primary && /^#[0-9a-f]{6}$/i.test(primary)) {
        return primary;
    }
    // Neutral RILE-Connect-ish blue fallback.
    return '#2C5F9E';
}
